import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { toast } from 'sonner';
import {
  applyExportFilters,
  buildAndDownloadInvoiceExport,
  validateExportFilters
} from '../utils/invoiceExport';

const STATUS_OPTIONS = [
  { value: 'pending_approval', label: 'Pending Function Head Approval' },
  { value: 'approved', label: 'PO Issued To Vendor' },
  { value: 'po_generated', label: 'Pending Accounts Approval' },
  { value: 'pending_finance_approval', label: 'Pending Finance Approval' },
  { value: 'tax_invoice_uploaded', label: 'Tax Invoice Uploaded' },
  { value: 'additional_info_requested', label: 'Addl Info Requested' },
  { value: 'finance_approved', label: 'Approved For Payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'on_hold', label: 'On Hold' }
];

const EMPTY_FILTERS = {
  entities: [],
  vendors: [],
  statuses: [],
  costCenters: [],
  createdFrom: '',
  createdTo: '',
  invoiceDateFrom: '',
  invoiceDateTo: ''
};

const MultiSelectList = ({ label, options, selected, onChange, getOptionValue, getOptionLabel }) => {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="border border-gray-300 rounded-md p-2 max-h-48 overflow-y-auto bg-white">
        {options.length === 0 ? (
          <p className="text-sm text-gray-500 p-2">No options available</p>
        ) : (
          options.map((option) => {
            const value = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            return (
              <label
                key={value}
                className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggle(value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm">{optionLabel}</span>
              </label>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-gray-600">{selected.length} selected</p>
      )}
    </div>
  );
};

const ExportInvoiceModal = ({ open, onOpenChange }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [entities, setEntities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [entitiesRes, vendorsRes, costCentresRes] = await Promise.all([
          axios.get(`${API}/entities/options`, { headers }),
          axios.get(`${API}/vendors`, { headers }),
          axios.get(`${API}/cost-centres`, { headers })
        ]);
        setEntities(entitiesRes.data || []);
        setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);
        setCostCenters(costCentresRes.data?.cost_centres || []);
      } catch (error) {
        console.error('Error loading export filter options:', error);
        toast.error('Failed to load filter options');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [open]);

  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      resetFilters();
    }
    onOpenChange(isOpen);
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    const validationError = validateExportFilters(filters);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [requestsRes, vendorsRes, roleEmailsRes] = await Promise.all([
        axios.get(`${API}/purchase-requests`, { headers }),
        axios.get(`${API}/vendors`, { headers }),
        axios.get(`${API}/users/role-emails`, { headers })
      ]);

      const requests = Array.isArray(requestsRes.data)
        ? requestsRes.data
        : (requestsRes.data.purchase_requests || []);
      const allVendors = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];
      const roleEmails = roleEmailsRes.data || {};
      const vendorsById = new Map(allVendors.map((v) => [v.id, v]));

      const filtered = applyExportFilters(requests, filters);

      if (!filtered.length) {
        toast.error('No invoices match the selected filters');
        return;
      }

      const success = buildAndDownloadInvoiceExport(filtered, vendorsById, roleEmails);
      if (success) {
        onOpenChange(false);
        resetFilters();
      }
    } catch (error) {
      console.error('Error exporting invoices:', error);
      toast.error('Failed to export invoices');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Export Invoices</DialogTitle>
          <DialogDescription>
            Leave filters empty to export all invoices.
          </DialogDescription>
        </DialogHeader>

        {loadingOptions ? (
          <p className="text-sm text-gray-500 py-4">Loading filter options...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
            <MultiSelectList
              label="Entity"
              options={entities}
              selected={filters.entities}
              onChange={(value) => updateFilter('entities', value)}
              getOptionValue={(e) => e.id}
              getOptionLabel={(e) => e.name}
            />
            <MultiSelectList
              label="Vendor"
              options={vendors}
              selected={filters.vendors}
              onChange={(value) => updateFilter('vendors', value)}
              getOptionValue={(v) => v.id}
              getOptionLabel={(v) => v.legal_entity_name || v.trade_name || v.id}
            />
            <MultiSelectList
              label="Status"
              options={STATUS_OPTIONS}
              selected={filters.statuses}
              onChange={(value) => updateFilter('statuses', value)}
              getOptionValue={(s) => s.value}
              getOptionLabel={(s) => s.label}
            />
            <MultiSelectList
              label="Cost Center"
              options={costCenters.map((cc) => ({ name: cc }))}
              selected={filters.costCenters}
              onChange={(value) => updateFilter('costCenters', value)}
              getOptionValue={(cc) => cc.name}
              getOptionLabel={(cc) => cc.name}
            />

            <div className="space-y-2">
              <Label>Created From</Label>
              <Input
                type="date"
                value={filters.createdFrom}
                onChange={(e) => updateFilter('createdFrom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Created To</Label>
              <Input
                type="date"
                value={filters.createdTo}
                onChange={(e) => updateFilter('createdTo', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Date From</Label>
              <Input
                type="date"
                value={filters.invoiceDateFrom}
                onChange={(e) => updateFilter('invoiceDateFrom', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Date To</Label>
              <Input
                type="date"
                value={filters.invoiceDateTo}
                onChange={(e) => updateFilter('invoiceDateTo', e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={exporting || loadingOptions}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportInvoiceModal;
